/**
 * @fileoverview Side Navigation Component
 * @module ctl-sidenav
 * @version 1.0.0
 * 
 * @description
 * A responsive sidebar navigation component with support for hierarchical menu
 * items, search filtering, collapsible state, and keyboard navigation. Displays
 * tooltips when collapsed for better accessibility.
 *
 * For mobile/responsive layouts (drawer pattern), use ctl-sidenav inside
 * ctl-app-shell, which automatically switches to an overlay drawer below the
 * configured breakpoint.
 * 
 * @example
 * <!-- Desktop/standalone usage -->
 * <ctl-sidenav id="nav" search collapsed>
 *   <div slot="header">Logo</div>
 *   <div slot="footer">Version 1.0</div>
 * </ctl-sidenav>
 * 
 * <!-- Mobile-responsive usage (recommended) -->
 * <ctl-app-shell variant="hybrid" breakpoint="lg">
 *   <ctl-topbar slot="topbar" title="My App"></ctl-topbar>
 *   <ctl-sidenav slot="sidenav" id="nav"></ctl-sidenav>
 *   <div slot="content">Main content</div>
 * </ctl-app-shell>
 * 
 * <script>
 *   const nav = document.getElementById('nav');
 *   nav.items = [
 *     { id: 'home', label: 'Home', icon: 'home', href: '/home' },
 *     { id: 'settings', label: 'Settings', icon: 'settings', href: '/settings' }
 *   ];
 *   nav.value = 'home';
 * </script>
 * 
 * @fires ctl-select - Fired when a navigation item is selected
 * @fires ctl-navigate - Fired when a navigation item with href is clicked
 * @fires ctl-toggle - Fired when the sidebar collapses/expands
 */

import "../foundation/ctl-icon.js";
import "../forms/ctl-text-field.js";
import "../foundation/ctl-divider.js";
import "../foundation/ctl-button.js";

const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    --ctl-sidenav-width: 264px;
    --ctl-sidenav-width-collapsed: 72px;

    display:block;
    box-sizing: border-box;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    color: var(--ctl-color-text);
  }

  nav{
    height: 100%;
    display:flex;
    flex-direction: column;
    background: var(--ctl-color-surface);
    border-right: 1px solid var(--ctl-color-border);
    width: var(--ctl-sidenav-width);
    transition: width var(--ctl-transition-normal);
  }
  :host([collapsed]) nav{
    width: var(--ctl-sidenav-width-collapsed);
  }

  .top{
    padding: var(--ctl-space-3) var(--ctl-space-3) var(--ctl-space-2);
    border-bottom: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    flex-shrink: 0;
  }

  .headerSlot::slotted(*){ display:block; }

  .controls{
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap: var(--ctl-space-2);
    margin-top: var(--ctl-space-2);
  }

  .collapseBtn{
    all: unset;
    cursor: pointer;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    width: var(--ctl-size-button-sm);
    height: var(--ctl-size-button-sm);
    min-height: 44px;
    min-width: 44px;
    border-radius: var(--ctl-radius-lg);
    color: var(--ctl-color-muted);
    background: transparent;
    background: color-mix(in srgb, var(--ctl-color-border) 25%, transparent);
    border: 1px solid var(--ctl-color-border);
    border: 1px solid color-mix(in srgb, var(--ctl-color-border) 70%, transparent);
    transition: background var(--ctl-transition-fast), color var(--ctl-transition-fast), transform var(--ctl-transition-fast), box-shadow var(--ctl-transition-fast);
  }
  .collapseBtn:hover{
    background: var(--ctl-color-surface-hover);
    background: color-mix(in srgb, var(--ctl-color-border) 42%, transparent);
    color: var(--ctl-color-text);
    color: color-mix(in srgb, var(--ctl-color-text) 90%, var(--ctl-color-muted));
    box-shadow: var(--ctl-shadow-sm);
    transform: scale(1.05);
  }
  .collapseBtn:active{
    transform: scale(0.98);
  }
  .collapseBtn:focus-visible{
    outline:none;
    box-shadow: var(--ctl-focus-ring);
  }

  .searchWrap{
    margin-top: var(--ctl-space-2);
  }
  :host(:not([search])) .searchWrap{ display:none; }
  :host([collapsed]) .searchWrap{ display:none; }

  .list{
    padding: var(--ctl-space-2) var(--ctl-space-2) var(--ctl-space-3);
    overflow: auto;
    flex: 1 1 auto;
  }

  .footer{
    padding: var(--ctl-space-3);
    border-top: 1px solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
    flex-shrink: 0;
  }
  .footer:empty{ display:none; }

  /* Items */
  .sectionLabel{
    padding: var(--ctl-space-3) var(--ctl-space-2) var(--ctl-space-2);
    font-size: var(--ctl-font-size-xs);
    letter-spacing: var(--ctl-tracking-widest);
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-muted);
    text-transform: uppercase;
  }
  :host([collapsed]) .sectionLabel{ display:none; }

  .item{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    width: 100%;
    padding: var(--ctl-space-2) var(--ctl-space-3);
    min-height: 44px;
    border-radius: var(--ctl-radius-lg);
    border: 1px solid transparent;
    background: transparent;
    color: var(--ctl-color-text);
    cursor: pointer;
    user-select: none;
    text-align:left;
    font: inherit;
    transition:
      background var(--ctl-transition-fast),
      border-color var(--ctl-transition-fast),
      color var(--ctl-transition-fast),
      transform var(--ctl-transition-fast),
      box-shadow var(--ctl-transition-fast);
    margin-bottom: var(--ctl-space-1);
    position: relative;
  }

  /* Tooltip - rendered as a fixed-position element via JS to escape overflow containers */
  .navTooltip {
    position: fixed;
    padding: var(--ctl-space-2) var(--ctl-space-3);
    background: var(--ctl-color-surface-inverse);
    color: var(--ctl-color-on-surface-inverse);
    border-radius: var(--ctl-radius-md);
    font-size: var(--ctl-font-size-sm);
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition: opacity var(--ctl-transition-fast), visibility var(--ctl-transition-fast);
    box-shadow: var(--ctl-shadow-lg);
    z-index: 10000;
  }
  .navTooltip[data-visible] {
    opacity: 1;
    visibility: visible;
  }

  .item:hover{
    background: var(--ctl-color-surface-hover);
    background: color-mix(in srgb, var(--ctl-color-brand) 10%, var(--ctl-color-surface));
    border-color: var(--ctl-color-border);
    border-color: color-mix(in srgb, var(--ctl-color-brand) 15%, var(--ctl-color-border));
    transform: translateX(1px);
  }

  .item:focus-visible{
    outline: none;
    box-shadow: var(--ctl-focus-ring);
  }

  .item[aria-current="page"]{
    background: var(--ctl-color-surface-hover);
    background: color-mix(in srgb, var(--ctl-color-brand) 15%, var(--ctl-color-surface));
    border-color: var(--ctl-color-brand);
    border-color: color-mix(in srgb, var(--ctl-color-brand) 30%, var(--ctl-color-border));
    color: var(--ctl-color-brand);
    font-weight: var(--ctl-font-weight-semibold);
    box-shadow: inset 3px 0 0 var(--ctl-color-brand), var(--ctl-shadow-sm);
  }

  .icon{
    width: var(--ctl-size-icon-md);
    height: var(--ctl-size-icon-md);
    color: var(--ctl-color-muted);
    color: color-mix(in srgb, var(--ctl-color-muted) 90%, var(--ctl-color-text));
    flex: 0 0 auto;
    display:inline-flex;
    align-items:center;
    justify-content:center;
  }
  
  .item[aria-current="page"] .icon{
    color: var(--ctl-color-brand);
  }

  .label{
    flex: 1 1 auto;
    min-width: 0;
    display:flex;
    flex-direction: column;
    gap: var(--ctl-space-1);
  }
  .title{
    font-weight: var(--ctl-font-weight-semibold);
    font-size: var(--ctl-font-size-sm);
    line-height: 1.2;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .desc{
    font-size: var(--ctl-font-size-xs);
    color: var(--ctl-color-muted);
    line-height: 1.2;
    overflow:hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :host([collapsed]) .label{ display:none; }

  .badge{
    font-size: var(--ctl-font-size-xs);
    font-weight: var(--ctl-font-weight-black);
    color: var(--ctl-color-muted);
    padding: var(--ctl-space-1) var(--ctl-space-2);
    border-radius: var(--ctl-radius-full);
    border: 1px solid var(--ctl-color-border);
    border: 1px solid color-mix(in srgb, var(--ctl-color-border) 85%, transparent);
    background: transparent;
    background: color-mix(in srgb, var(--ctl-color-border) 25%, transparent);
    flex: 0 0 auto;
  }
  :host([collapsed]) .badge{ display:none; }

  /* Groups */  .group{
    margin-top: var(--ctl-space-2);
  }

  .groupBtn{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2);
    width: 100%;
    padding: var(--ctl-space-2) var(--ctl-space-2);
    min-height: 44px;
    border-radius: var(--ctl-radius-lg);
    border: 1px solid transparent;
    background: transparent;
    cursor: pointer;
    font: inherit;
    text-align:left;
    color: var(--ctl-color-text);
    transition: background var(--ctl-transition-fast), border-color var(--ctl-transition-fast);
    position: relative;
  }
  
  .groupBtn:hover{
    background: var(--ctl-color-surface-hover);
    background: color-mix(in srgb, var(--ctl-color-border) 25%, transparent);
    border-color: var(--ctl-color-border);
    border-color: color-mix(in srgb, var(--ctl-color-border) 80%, transparent);
  }
  .groupBtn:focus-visible{
    outline:none;
    box-shadow: var(--ctl-focus-ring);
  }

  .chev{
    width: var(--ctl-size-icon-sm);
    height: var(--ctl-size-icon-sm);
    color: var(--ctl-color-muted);
    flex: 0 0 auto;
    transition: transform var(--ctl-transition-fast);
  }
  .group[data-open="true"] .chev{ transform: rotate(90deg); }

  :host([collapsed]) .chev{ display:none; }

  .children{
    padding-left: var(--ctl-space-3);
    margin-top: var(--ctl-space-2);
    display:none;
  }
  .group[data-open="true"] .children{ display:block; }
  :host([collapsed]) .children{ display:none; }

  .child .item{
    padding: var(--ctl-space-2) var(--ctl-space-2);
  }

  /* Reduce motion for accessibility */
  @media (prefers-reduced-motion: reduce) {
    .item,
    .collapseBtn,
    .groupBtn,
    .chev,
    .navTooltip {
      transition: none;
    }
  }
</style>

<nav part="nav" aria-label="Sidebar">
  <div class="top" part="top">
    <div class="headerSlot" part="header">
      <slot name="header"></slot>
    </div>

    <div class="controls" part="controls">
      <div style="min-width:0; flex:1 1 auto;"></div>
      <button class="collapseBtn" id="toggle" type="button" aria-label="Collapse sidebar">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="2" y="3.25" width="12" height="1.5" rx="0.75" fill="currentColor"/>
          <rect x="2" y="7.25" width="12" height="1.5" rx="0.75" fill="currentColor"/>
          <rect x="2" y="11.25" width="12" height="1.5" rx="0.75" fill="currentColor"/>
        </svg>
      </button>
    </div>

    <div class="searchWrap" part="search">
      <ctl-text-field id="search" placeholder="Search…" clearable></ctl-text-field>
    </div>
  </div>

  <div class="list" part="list">
    <div id="items"></div>
    <slot id="itemSlot" name="item"></slot>
  </div>

  <div class="footer" part="footer"><slot name="footer"></slot></div>

  <div class="navTooltip" id="navTooltip" role="tooltip" aria-hidden="true"></div>
</nav>
`;

function toArray(v) { return Array.isArray(v) ? v : []; }
function escapeHtml(s) {
  return String(s ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function normalizeItems(items) {
  const arr = toArray(items);
  const norm = [];

  for (const it of arr) {
    if (!it || typeof it !== "object") continue;

    const id = it.id != null ? String(it.id) : "";
    const label = String(it.label ?? it.title ?? it.name ?? "");
    const href = it.href != null ? String(it.href) : null;

    const node = {
      id: id || (href ? href : label ? label : ""),
      label,
      description: it.description != null ? String(it.description) : "",
      icon: it.icon != null ? String(it.icon) : "",
      href,
      badge: it.badge != null ? String(it.badge) : "",
      disabled: !!it.disabled,
      section: it.section != null ? String(it.section) : "",
      open: it.open != null ? !!it.open : false,
      children: []
    };

    // Children (groups)
    if (Array.isArray(it.children) && it.children.length) {
      node.children = normalizeItems(it.children).map((c) => ({
        ...c,
        section: "" // sections handled at top-level
      }));
    }

    // If no stable id could be derived, skip.
    if (!node.id) continue;

    norm.push(node);
  }

  return norm;
}

const ICON_HAMBURGER = '<rect x="2" y="3.25" width="12" height="1.5" rx="0.75" fill="currentColor"/><rect x="2" y="7.25" width="12" height="1.5" rx="0.75" fill="currentColor"/><rect x="2" y="11.25" width="12" height="1.5" rx="0.75" fill="currentColor"/>';
const ICON_CHEVRON_RIGHT = '<path d="M6 3.5L10.5 8 6 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>';

/**
 * CtlSidenav - Side Navigation Component
 * 
 * @class
 * @extends HTMLElement
 * 
 * @attr {string} value - ID of the currently selected navigation item
 * @attr {boolean} collapsed - Whether the sidebar is collapsed
 * @attr {boolean} search - Whether to show the search field
 * 
 * @property {Array} items - Array of navigation items with structure:
 *   { id, label, icon?, href?, badge?, disabled?, section?, children? }
 * @property {string|null} value - Currently selected item ID
 * 
 * @slot header - Header content (e.g., logo)
 * @slot footer - Footer content (e.g., version info)
 * @slot item - Custom item templates (use data-id attribute)
 */
export class CtlSidenav extends HTMLElement {
  static get observedAttributes() {
    return ["value", "collapsed", "search"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));

    this._itemsEl = this.shadowRoot.getElementById("items");
    this._toggleBtn = this.shadowRoot.getElementById("toggle");
    this._searchField = this.shadowRoot.getElementById("search");
    this._itemSlot = this.shadowRoot.getElementById("itemSlot");
    this._tooltipEl = this.shadowRoot.getElementById("navTooltip");

    this._items = [];
    this._value = null;
    this._filter = "";
    this._rovingId = null;

    this._itemTemplates = new Map(); // data-id => template
    this._tooltipTarget = null;
    this._onClick = this._onClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onSlotChange = this._onSlotChange.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._onSearchInput = this._onSearchInput.bind(this);
    this._onMouseEnter = this._onMouseEnter.bind(this);
    this._onMouseLeave = this._onMouseLeave.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
  }

  connectedCallback() {
    // hydrate value from attr
    if (this.hasAttribute("value")) this._value = this.getAttribute("value");

    this.shadowRoot.addEventListener("click", this._onClick);
    this.shadowRoot.addEventListener("keydown", this._onKeyDown);
    this._toggleBtn.addEventListener("click", this._onToggle);
    this._updateToggleIcon();

    // Template slot hook
    this._itemSlot.addEventListener("slotchange", this._onSlotChange);
    this._collectTemplates();

    // search
    this._attachSearch();

    // Tooltip events (mouseenter/mouseleave use capture to cover shadow children)
    this.shadowRoot.addEventListener("mouseover", this._onMouseEnter);
    this.shadowRoot.addEventListener("mouseout", this._onMouseLeave);
    this.shadowRoot.addEventListener("focusin", this._onFocusIn);
    this.shadowRoot.addEventListener("focusout", this._onFocusOut);

    this._render();
  }

  disconnectedCallback() {
    this.shadowRoot.removeEventListener("click", this._onClick);
    this.shadowRoot.removeEventListener("keydown", this._onKeyDown);
    this._toggleBtn.removeEventListener("click", this._onToggle);
    this._itemSlot.removeEventListener("slotchange", this._onSlotChange);
    this._detachSearch();
    this.shadowRoot.removeEventListener("mouseover", this._onMouseEnter);
    this.shadowRoot.removeEventListener("mouseout", this._onMouseLeave);
    this.shadowRoot.removeEventListener("focusin", this._onFocusIn);
    this.shadowRoot.removeEventListener("focusout", this._onFocusOut);
    this._hideTooltip();
  }

  attributeChangedCallback(name) {
    if (name === "value") this._value = this.getAttribute("value");
    if (name === "collapsed") {
      this._updateToggleIcon();
      this.dispatchEvent(new CustomEvent("ctl-toggle", {
        bubbles: true,
        composed: true,
        detail: { collapsed: this.collapsed }
      }));
    }
    if (name === "search") {
      this._attachSearch();
      this._render();
    }
    this._render();
  }

  // ---- attrs ----
  get value() { return this._value; }
  set value(v) {
    const next = (v == null || v === "") ? null : String(v);
    this._value = next;
    if (next == null) this.removeAttribute("value");
    else this.setAttribute("value", next);
    this._render();
  }

  get collapsed() { return this.hasAttribute("collapsed"); }
  set collapsed(v) { v ? this.setAttribute("collapsed", "") : this.removeAttribute("collapsed"); }

  get search() { return this.hasAttribute("search"); }
  set search(v) { v ? this.setAttribute("search", "") : this.removeAttribute("search"); }

  // ---- props ----
  get items() { return this._items; }
  set items(v) {
    this._items = normalizeItems(v);
    this._render();
  }

  // ---- templates ----
  _onSlotChange() {
    this._collectTemplates();
    this._render();
  }

  _collectTemplates() {
    this._itemTemplates.clear();
    const els = this._itemSlot.assignedElements({ flatten: true }) || [];
    for (const el of els) {
      if (el.tagName !== "TEMPLATE") continue;
      const key = el.getAttribute("data-id") || "*";
      this._itemTemplates.set(key, el);
    }
  }

  _renderTemplate(tmpl, data) {
    const html = tmpl.innerHTML.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, key) => {
      const v = data[key];
      return escapeHtml(v == null ? "" : String(v));
    });
    return html;
  }

  // ---- search ----
  _attachSearch() {
    this._detachSearch();
    if (!this.search) return;

    // ctl-text-field should dispatch 'input' with value accessible
    this._searchField?.addEventListener("input", this._onSearchInput);
    this._searchField?.addEventListener("ctl-input", this._onSearchInput);
  }

  _detachSearch() {
    this._searchField?.removeEventListener("input", this._onSearchInput);
    this._searchField?.removeEventListener("ctl-input", this._onSearchInput);
  }

  _onSearchInput(e) {
    const tf = this._searchField;
    const v =
      (e?.detail && typeof e.detail.value === "string" ? e.detail.value :
      (tf && typeof tf.value === "string" ? tf.value :
      (tf ? tf.getAttribute("value") : ""))) || "";

    this._filter = String(v).trim().toLowerCase();
    this._render();
  }

  // ---- render ----
  _render() {
    const items = this._applyFilter(this._items, this._filter);

    // Group by section label (top-level only)
    const sections = new Map();
    for (const it of items) {
      const key = it.section || "";
      if (!sections.has(key)) sections.set(key, []);
      sections.get(key).push(it);
    }

    const out = [];
    let sectionIndex = 0;

    for (const [section, list] of sections.entries()) {
      if (section) {
        const sectionId = `section-${sectionIndex++}`;
        out.push(`<div class="sectionLabel" part="section" id="${sectionId}" role="heading" aria-level="2">${escapeHtml(section)}</div>`);
      }
      for (const it of list) out.push(this._renderNode(it, 0));
    }

    this._itemsEl.innerHTML = out.join("");
    this._syncAriaCurrent();
    this._syncTabindex();
  }

  _applyFilter(items, q) {
    if (!q) return items;

    const match = (it) => {
      const hay = `${it.label} ${it.description} ${it.badge}`.toLowerCase();
      return hay.includes(q);
    };

    const filtered = [];
    for (const it of items) {
      if (it.children && it.children.length) {
        const kids = this._applyFilter(it.children, q);
        if (match(it) || kids.length) {
          filtered.push({ ...it, open: true, children: kids });
        }
      } else {
        if (match(it)) filtered.push(it);
      }
    }
    return filtered;
  }

  _renderNode(it, depth) {
    const selected = this._value != null && String(this._value) === String(it.id);
    const hasChildren = Array.isArray(it.children) && it.children.length > 0;

    // Custom item renderer template hook (by id or wildcard)
    const tmpl = this._itemTemplates.get(it.id) || this._itemTemplates.get("*");

    if (!hasChildren) {
      const body = tmpl
        ? this._renderTemplate(tmpl, {
            id: it.id,
            label: it.label,
            description: it.description,
            href: it.href || "",
            icon: it.icon || "",
            badge: it.badge || "",
            selected: selected ? "true" : "false",
            disabled: it.disabled ? "true" : "false"
          })
        : this._defaultItemHtml(it, selected);

      return `
        <div class="leaf" part="leaf">
          ${body}
        </div>
      `;
    }

    // Group node
    const open = this._isGroupOpen(it);
    const groupBtn = tmpl
      ? this._renderTemplate(tmpl, {
          id: it.id,
          label: it.label,
          description: it.description,
          href: it.href || "",
          icon: it.icon || "",
          badge: it.badge || "",
          selected: selected ? "true" : "false",
          disabled: it.disabled ? "true" : "false",
          group: "true",
          open: open ? "true" : "false"
        })
      : this._defaultGroupBtnHtml(it, open);

    const kids = it.children.map((c) => `<div class="child" part="child">${this._renderNode(c, depth + 1)}</div>`).join("");
    const childrenId = `children-${escapeHtml(it.id)}`;

    return `
      <div class="group" part="group" data-group="${escapeHtml(it.id)}" data-open="${open ? "true" : "false"}">
        ${groupBtn}
        <div class="children" part="children" id="${childrenId}">${kids}</div>
      </div>
    `;
  }

  _defaultItemHtml(it, selected) {
    const icon = it.icon ? `<ctl-icon class="icon" name="${escapeHtml(it.icon)}" aria-hidden></ctl-icon>` : `<span class="icon" aria-hidden></span>`;
    const badge = it.badge ? `<span class="badge" aria-hidden="true">${escapeHtml(it.badge)}</span>` : "";
    const desc = it.description ? `<div class="desc">${escapeHtml(it.description)}</div>` : "";

    return `
      <button
        class="item"
        part="item"
        type="button"
        data-role="item"
        data-id="${escapeHtml(it.id)}"
        data-label="${escapeHtml(it.label)}"
        aria-label="${escapeHtml(it.label)}"
        tabindex="-1"
        ${it.href ? `data-href="${escapeHtml(it.href)}"` : ""}
        ${it.disabled ? "disabled" : ""}
        aria-current="${selected ? "page" : "false"}"
      >
        ${icon}
        <span class="label">
          <span class="title">${escapeHtml(it.label)}</span>
          ${desc}
        </span>
        ${badge}
      </button>
    `;
  }

  _defaultGroupBtnHtml(it, open) {
    const icon = it.icon ? `<ctl-icon class="icon" name="${escapeHtml(it.icon)}" aria-hidden></ctl-icon>` : `<span class="icon" aria-hidden></span>`;
    const badge = it.badge ? `<span class="badge" aria-hidden="true">${escapeHtml(it.badge)}</span>` : "";
    const desc = it.description ? `<div class="desc">${escapeHtml(it.description)}</div>` : "";
    const childrenId = `children-${escapeHtml(it.id)}`;

    return `
      <button
        class="groupBtn"
        part="groupBtn"
        type="button"
        data-role="group"
        data-id="${escapeHtml(it.id)}"
        data-label="${escapeHtml(it.label)}"
        aria-label="${escapeHtml(it.label)}"
        tabindex="-1"
        ${it.disabled ? "disabled" : ""}
        aria-expanded="${open ? "true" : "false"}"
        aria-controls="${childrenId}"
      >
        ${icon}
        <span class="label">
          <span class="title">${escapeHtml(it.label)}</span>
          ${desc}
        </span>
        ${badge}
        <svg class="chev" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
  }

  _syncAriaCurrent() {
    const btns = this.shadowRoot.querySelectorAll('button[data-role="item"]');
    for (const b of btns) {
      const id = b.dataset.id;
      const selected = this._value != null && String(this._value) === String(id);
      b.setAttribute("aria-current", selected ? "page" : "false");
    }
  }

  _syncTabindex() {
    const all = Array.from(
      this.shadowRoot.querySelectorAll('button[data-role="item"], button[data-role="group"]')
    ).filter((b) => !b.disabled);

    if (!all.length) return;

    // Only consider *visible* buttons for roving tabindex, to avoid focusing
    // items that are hidden (e.g., in collapsed groups or when the host is collapsed).
    const visible = this._isVisible
      ? all.filter((b) => this._isVisible(b))
      : all;

    // If no buttons are visible, make all untabbable and clear roving state.
    if (!visible.length) {
      for (const b of all) {
        b.setAttribute("tabindex", "-1");
      }
      this._rovingId = null;
      return;
    }

    // Determine which button should be in the tab sequence (tabindex="0"):
    // 1. The last item the user interacted with (_rovingId), if still present and visible.
    // 2. The currently selected item, if available and visible.
    // 3. The first visible enabled button as a fallback.
    let target = null;
    if (this._rovingId !== null) {
      target = visible.find((b) => b.dataset.id === this._rovingId) || null;
    }
    if (!target && this._value !== null) {
      target =
        visible.find(
          (b) => b.dataset.role === "item" && b.dataset.id === this._value
        ) || null;
    }
    if (!target) target = visible[0];

    for (const b of all) {
      b.setAttribute("tabindex", b === target ? "0" : "-1");
    }
    this._rovingId = target.dataset.id;
  }

  _isGroupOpen(it) {
    // open if explicitly set, or if any descendant matches current selection
    if (it.open) return true;
    if (this._value == null) return false;

    const target = String(this._value);
    const walk = (node) => {
      for (const c of node.children || []) {
        if (String(c.id) === target) return true;
        if (c.children && c.children.length && walk(c)) return true;
      }
      return false;
    };
    return walk(it);
  }

  // ---- events / interaction ----
  _updateToggleIcon() {
    if (!this._toggleBtn) return;
    const collapsed = this.collapsed;
    this._toggleBtn.setAttribute("aria-label", collapsed ? "Expand sidebar" : "Collapse sidebar");
    const svg = this._toggleBtn.querySelector("svg");
    if (!svg) return;
    if (collapsed) {
      svg.innerHTML = ICON_CHEVRON_RIGHT;
    } else {
      svg.innerHTML = ICON_HAMBURGER;
    }
  }

  _onToggle(e) {
    e.preventDefault();
    // Setting collapsed triggers attributeChangedCallback, which updates
    // aria-label and dispatches ctl-toggle — no need to duplicate here.
    this.collapsed = !this.collapsed;
  }

  _onClick(e) {
    const path = e.composedPath ? e.composedPath() : [];
    const btn = this._findInPath(path, (el) => el?.tagName === "BUTTON" && el?.dataset?.role);
    if (!btn) return;

    const role = btn.dataset.role;
    const id = btn.dataset.id;

    if (role === "group") {
      // toggle group open
      const group = this.shadowRoot.querySelector(`.group[data-group="${CSS.escape(id)}"]`);
      if (group) {
        const open = group.getAttribute("data-open") === "true";
        group.setAttribute("data-open", open ? "false" : "true");
        btn.setAttribute("aria-expanded", open ? "false" : "true");
      }
      // Update roving tabindex to this button
      this._rovingId = id;
      this._syncTabindex();
      return;
    }

    if (role === "item") {
      const item = this._findItemById(id);
      if (!item || item.disabled) return;

      // Update roving tabindex before re-render so _syncTabindex picks it up
      this._rovingId = id;

      // update selection
      this.value = id;

      this.dispatchEvent(new CustomEvent("ctl-select", {
        bubbles: true,
        composed: true,
        detail: { id, item }
      }));

      const href = btn.dataset.href;
      if (href) {
        this.dispatchEvent(new CustomEvent("ctl-navigate", {
          bubbles: true,
          composed: true,
          detail: { href, item }
        }));
      }
    }
  }

  _onKeyDown(e) {
    // Roving tabindex within list: ArrowUp/Down/Home/End
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;

    const active = this.shadowRoot.activeElement;
    const focusables = Array.from(this.shadowRoot.querySelectorAll('button[data-role="item"], button[data-role="group"]'))
      .filter((b) => !b.disabled && this._isVisible(b));

    const idx = focusables.indexOf(active);
    if (idx < 0) return;

    e.preventDefault();
    let next;
    if (e.key === "ArrowDown") next = focusables[Math.min(focusables.length - 1, idx + 1)];
    else if (e.key === "ArrowUp") next = focusables[Math.max(0, idx - 1)];
    else if (e.key === "Home") next = focusables[0];
    else if (e.key === "End") next = focusables[focusables.length - 1];

    if (next && next !== active) {
      this._rovingId = next.dataset.id;
      this._syncTabindex();
    }
    next?.focus();
  }

  // ---- tooltip ----
  _showTooltip(btn) {
    if (!this.collapsed || !this._tooltipEl) return;
    const label = btn.dataset.label;
    if (!label) return;
    const rect = btn.getBoundingClientRect();
    this._tooltipEl.textContent = label;
    // Initial position: to the right of the button, vertically centered
    this._tooltipEl.style.left = `${rect.right + 8}px`;
    this._tooltipEl.style.top = `${rect.top + rect.height / 2}px`;
    this._tooltipEl.style.transform = "translateY(-50%)";
    this._tooltipEl.setAttribute("data-visible", "");
    this._tooltipEl.removeAttribute("aria-hidden");
    // Viewport boundary detection — adjust after rendering so getBoundingClientRect is accurate
    const tipRect = this._tooltipEl.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    // Overflow on the right → flip to the left of the button, clamped within viewport with margin
    if (tipRect.right > vw) {
      const desiredLeft = rect.left - tipRect.width - margin;
      const clampedLeft = Math.max(margin, Math.min(desiredLeft, vw - tipRect.width - margin));
      this._tooltipEl.style.left = `${clampedLeft}px`;
    }
    // Overflow on the bottom → shift upward so the bottom stays within viewport, clamped with margin
    if (tipRect.bottom > vh) {
      const clampedTop = Math.max(margin, vh - tipRect.height - margin);
      this._tooltipEl.style.top = `${clampedTop}px`;
      this._tooltipEl.style.transform = "none";
    }
    // Overflow on the top → shift downward so the top aligns with the viewport edge
    if (tipRect.top < 0) {
      this._tooltipEl.style.top = "0px";
      this._tooltipEl.style.transform = "none";
    }
  }

  _hideTooltip() {
    if (!this._tooltipEl) return;
    this._tooltipTarget = null;
    this._tooltipEl.removeAttribute("data-visible");
    this._tooltipEl.setAttribute("aria-hidden", "true");
  }

  _onMouseEnter(e) {
    const btn = e.target?.closest?.("button[data-label]");
    if (btn) {
      if (this._tooltipTarget !== btn) {
        this._tooltipTarget = btn;
        this._showTooltip(btn);
      }
    }
  }

  _onMouseLeave(e) {
    // relatedTarget is where the mouse moved to; if it's still inside the same button, ignore
    const related = e.relatedTarget;
    const btn = e.target?.closest?.("button[data-label]");
    if (btn && related && btn.contains(related)) return;
    this._tooltipTarget = null;
    this._hideTooltip();
  }

  _onFocusIn(e) {
    const btn = e.target?.closest?.("button[data-label]");
    if (btn) this._showTooltip(btn);
  }

  _onFocusOut(e) {
    const related = e.relatedTarget;

    // If focus is leaving the document or there is no next target, hide the tooltip.
    if (!related) {
      this._hideTooltip();
      return;
    }

    // If focus moved to another tooltip-triggering button within this component,
    // keep the tooltip visible; the subsequent focus-in handler will update it.
    const relatedBtn = related.closest?.("button[data-label]");
    if (relatedBtn && this.contains(relatedBtn)) {
      return;
    }

    // If focus moved into the tooltip element or any of its descendants, do not hide.
    if (this._tooltipEl && this._tooltipEl.contains(related)) {
      return;
    }
    this._hideTooltip();
  }

  _isVisible(el) {
    // group children are display:none when group closed; offsetParent null is a cheap visibility proxy
    return !!(el && el.offsetParent !== null);
  }

  _findItemById(id) {
    const walk = (arr) => {
      for (const it of arr) {
        if (String(it.id) === String(id)) return it;
        if (it.children && it.children.length) {
          const found = walk(it.children);
          if (found) return found;
        }
      }
      return null;
    };
    return walk(this._items);
  }

  _findInPath(path, predicate) {
    for (const el of path) {
      if (predicate(el)) return el;
    }
    return null;
  }
}

customElements.define("ctl-sidenav", CtlSidenav);
